// Platform announcements (the bell in the top bar): the pure parts, kept out
// of the components so they can be tested on their own.
//
// Times travel as UTC from the backend and are always shown in Greek time,
// day/month/year, whatever the viewer's computer says. The admin types times
// in Greek time too, and they are sent without an offset ("2026-10-13T21:00");
// the backend reads that as Europe/Athens.

export const ATHENS_TZ = "Europe/Athens";
export const ANNOUNCEMENT_KINDS = ["info", "maintenance", "important"];
export const TITLE_MAX = 120;
export const BODY_MAX = 1000;
export const POLL_MS = 60 * 1000;

const DATE_TIME = new Intl.DateTimeFormat("en-GB", {
  timeZone: ATHENS_TZ,
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
});

/** "13/10/2026, 21:00" in Greek time, or "—". */
export function formatGreekDateTime(value) {
  if (!value) return "—";
  const date = value instanceof Date ? value : new Date(value);
  return Number.isFinite(date.getTime()) ? DATE_TIME.format(date) : "—";
}

/** The year, month, day, hour and minute it is now in Greece. */
export function athensParts(date = new Date()) {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat("en-GB", {
      timeZone: ATHENS_TZ,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    }).formatToParts(date).map((p) => [p.type, p.value]),
  );
  return {
    date: `${parts.year}-${parts.month}-${parts.day}`,
    hour: parts.hour,
    minute: parts.minute,
  };
}

/** "2026-10-13" + "21" + "00" -> "2026-10-13T21:00" (Greek time, no offset). */
export function greekLocalValue(date, hour, minute) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(date || ""))) return "";
  const hh = String(hour ?? "").padStart(2, "0");
  const mm = String(minute ?? "").padStart(2, "0");
  if (!/^([01]\d|2[0-3])$/.test(hh) || !/^[0-5]\d$/.test(mm)) return "";
  return `${date}T${hh}:${mm}`;
}

/** Same ordering as the strings, because both are Greek wall-clock time. */
export function isAfter(localA, localB) {
  return Boolean(localA && localB && localA > localB);
}

/** English only when the admin filled in both English fields. */
export function announcementText(item, isGreek) {
  const english = !isGreek && item?.title_en && item?.body_en;
  return {
    title: english ? item.title_en : item?.title_el || "",
    body: english ? item.body_en : item?.body_el || "",
  };
}

/** What the admin form checks before it lets "Publish" be pressed. */
export function draftProblems(draft, nowLocal) {
  const problems = [];
  const titleEl = (draft.title_el || "").trim();
  const bodyEl = (draft.body_el || "").trim();
  const titleEn = (draft.title_en || "").trim();
  const bodyEn = (draft.body_en || "").trim();
  if (!titleEl) problems.push("title_el_missing");
  if (!bodyEl) problems.push("body_el_missing");
  if (Boolean(titleEn) !== Boolean(bodyEn)) problems.push("english_incomplete");
  if (titleEl.length > TITLE_MAX || titleEn.length > TITLE_MAX) problems.push("title_too_long");
  if (bodyEl.length > BODY_MAX || bodyEn.length > BODY_MAX) problems.push("body_too_long");
  const start = draft.startNow ? nowLocal : draft.starts_at;
  if (!draft.startNow && !draft.starts_at) problems.push("starts_at_missing");
  if (draft.endDate && !draft.ends_at) problems.push("ends_at_invalid");
  if (draft.ends_at && !isAfter(draft.ends_at, nowLocal)) problems.push("ends_in_past");
  else if (draft.ends_at && start && !isAfter(draft.ends_at, start)) problems.push("window_invalid");
  return problems;
}
